import {
  EvidenceTypeEnum,
  EvidenceUploadMode,
  getApiInspectionsV1EvidenceListQueryKey,
  getApiLogisticsV1LoadsEvidenceListQueryKey,
  getApiQualityExternalLoadsV1ExternalLoadsEvidenceListQueryKey,
  getApiQualitySessionsV2TradeCompanySessionEvidenceListQueryKey,
  getApiSamplesV1SamplesEvidenceListQueryKey,
  getApiTradeV1SiteMaterialsEvidenceListQueryKey,
  PaginatedEvidenceList,
  useApiInspectionsV1EvidenceList,
  useApiLogisticsV1LoadsEvidenceList,
  useApiQualityExternalLoadsV1ExternalLoadsEvidenceList,
  useApiQualitySessionsV2TradeCompanySessionEvidenceList,
  useApiSamplesV1SamplesEvidenceList,
  useApiTradeV1SiteMaterialsEvidenceList,
  useQueryClient,
} from 'api';
import { logger } from 'monitor';
import { useCallback } from 'react';

export const DEFAULT_EVIDENCE_SORT_ORDER = '-created_at' as const;
export const defaultOptions = {
  order: DEFAULT_EVIDENCE_SORT_ORDER,
  pageSize: 25,
};

export const useGetEvidenceList = ({
  mode,
  parentId,
  options = defaultOptions,
  enabled = true,
}: {
  mode?: EvidenceUploadMode;
  parentId: string | number;
  options?: {
    page_size?: number;
    type?: EvidenceTypeEnum;
    page?: number;
    order?: 'created_at' | '-created_at' | 'id' | '-id' | 'type' | '-type';
    include_ids?: number[];
  };
  enabled?: boolean;
}) => {
  const hasParent = Boolean(parentId);
  const hasUploadMode = Boolean(mode);

  const queryClient = useQueryClient();
  const {
    data: siteMaterialEvidence,
    isLoading: isSiteMaterialLoading,
    isFetched: isSiteMaterialFetched,
  } = useApiTradeV1SiteMaterialsEvidenceList(String(parentId), options, {
    query: {
      enabled: enabled && mode === 'site-material' && hasParent && hasUploadMode,
    },
  });

  const {
    data: loadEvidence,
    isLoading: isLoadLoading,
    isFetched: isLoadFetched,
  } = useApiLogisticsV1LoadsEvidenceList(String(parentId), options, {
    query: {
      enabled: enabled && mode === 'load' && hasParent && hasUploadMode,
    },
  });

  const {
    data: tradeCompanySessionEvidence,
    isLoading: isTradeCompanySessionLoading,
    isFetched: isTradeCompanySessionFetched,
  } = useApiQualitySessionsV2TradeCompanySessionEvidenceList(Number(parentId), options, {
    query: {
      enabled: enabled && mode === 'trade-company-session' && hasParent && hasUploadMode,
    },
  });

  const {
    data: sampleEvidence,
    isLoading: isSampleLoading,
    isFetched: isSampleFetched,
  } = useApiSamplesV1SamplesEvidenceList(Number(parentId), options, {
    query: {
      enabled: enabled && mode === 'sample' && hasParent && hasUploadMode,
    },
  });

  const {
    data: inspectionEvidence,
    isLoading: isInspectionLoading,
    isFetched: isInspectionFetched,
  } = useApiInspectionsV1EvidenceList(String(parentId), options, {
    query: {
      enabled: enabled && mode === 'inspector' && hasParent && hasUploadMode,
    },
  });

  const {
    data: externalLoadEvidence,
    isLoading: isExternalLoadLoading,
    isFetched: isExternalLoadFetched,
  } = useApiQualityExternalLoadsV1ExternalLoadsEvidenceList(Number(parentId), options, {
    query: {
      enabled: enabled && mode === 'external-load' && hasParent && hasUploadMode,
    },
  });

  const modeConfig: Record<
    string,
    { data: PaginatedEvidenceList | undefined; isLoading: boolean; isFetched: boolean }
  > = {
    'external-load': {
      data: externalLoadEvidence,
      isFetched: isExternalLoadFetched,
      isLoading: isExternalLoadLoading,
    },
    inspector: { data: inspectionEvidence, isFetched: isInspectionFetched, isLoading: isInspectionLoading },
    load: { data: loadEvidence, isFetched: isLoadFetched, isLoading: isLoadLoading },
    sample: { data: sampleEvidence, isFetched: isSampleFetched, isLoading: isSampleLoading },
    'site-material': { data: siteMaterialEvidence, isFetched: isSiteMaterialFetched, isLoading: isSiteMaterialLoading },
    'trade-company-session': {
      data: tradeCompanySessionEvidence,
      isFetched: isTradeCompanySessionFetched,
      isLoading: isTradeCompanySessionLoading,
    },
  };
  const data = mode ? modeConfig[mode]?.data : undefined;
  const isLoading = mode ? modeConfig[mode]?.isLoading : false;
  const isFetched = mode ? modeConfig[mode]?.isFetched : false;

  const refetchEvidence = useCallback(
    (parentIdOverride?: number | string, refetchAllEvidence?: boolean) => {
      if (!hasUploadMode) {
        logger.warn('No upload mode specified for refetching evidence');
        return;
      }
      const refetchOptions = refetchAllEvidence ? undefined : options;
      const effectiveParentId = parentIdOverride ?? parentId;
      logger.info(`Refetching evidence for mode ${mode} and parentId ${effectiveParentId} with options`, {
        refetchOptions,
      });

      switch (mode) {
        case 'inspector':
          queryClient.invalidateQueries({
            queryKey: getApiInspectionsV1EvidenceListQueryKey(String(effectiveParentId), refetchOptions),
          });
          break;
        case 'load':
          queryClient.invalidateQueries({
            queryKey: getApiLogisticsV1LoadsEvidenceListQueryKey(String(effectiveParentId), refetchOptions),
          });
          break;
        case 'sample':
          queryClient.invalidateQueries({
            queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(Number(effectiveParentId), refetchOptions),
          });
          break;
        case 'site-material':
          queryClient.invalidateQueries({
            queryKey: getApiTradeV1SiteMaterialsEvidenceListQueryKey(String(effectiveParentId), refetchOptions),
          });
          break;
        case 'trade-company-session':
          queryClient.invalidateQueries({
            queryKey: getApiQualitySessionsV2TradeCompanySessionEvidenceListQueryKey(
              Number(effectiveParentId),
              refetchOptions
            ),
          });
          break;

        case 'external-load':
          queryClient.invalidateQueries({
            queryKey: getApiQualityExternalLoadsV1ExternalLoadsEvidenceListQueryKey(
              Number(effectiveParentId),
              refetchOptions
            ),
          });
          break;
      }
    },
    [queryClient, hasUploadMode, mode, parentId, options]
  );

  return {
    count: data?.count,
    data: data?.results,
    isFetched,
    isLoading,
    next: data?.next,
    refetchEvidence,
  };
};
